import React, { useEffect, useState } from "react"
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
  Badge,
  Input,
  Button,
  UncontrolledTooltip
} from "reactstrap"
import { adjustCameraZoom } from "helpers/camera_utility"
import { RefreshCw, HardDrive, Database, Percent, AlertTriangle, Folder } from 'lucide-react'


// Function to map battery status to color
const getDiskStatusColor = status => {
  switch (status) {
    case "OK":
      return "success"
    case "connected":
      return "warning"
    default:
      return "danger"
  }
}

const AxisCameraDeviceCard = ({ camera, shouldResetZoom = false }) => {
  const [cameraZoom, setCameraZoom] = useState(0.1)
  const [isZoomedRerset, setIsZoomedReset] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState(null);
  const [refreshPreviewKey, setRefreshPreviewKey] = useState(0);
  const [diskSpaceWarningText, setDiskSpaceWarningText] = useState("");


  const refreshPreview = () => {
    setRefreshPreviewKey(refreshPreviewKey + 1);
  }

  const handleZoomChange = (event) => {
    const newZoomValue = parseFloat(event.target.value);
    setCameraZoom(newZoomValue);

    // Clear the previous timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const newTimer = setTimeout(async () => {
      const apiZoomValue = Math.round(newZoomValue * 9998) + 1; // scale 0-1 to 1-9999
      await adjustCameraZoom(camera?.details?.hostname, apiZoomValue);
    }, 400);

    setDebounceTimer(newTimer);
  };

  // Set the camera zoom to 1 on the first page load
  useEffect(() => {
    // Create an async function
    const resetZoom = async () => {
      if (camera && !isZoomedRerset && shouldResetZoom) {
        await adjustCameraZoom(camera?.details?.hostname, 1000); // 10%
        setIsZoomedReset(true);
      }
    };

    resetZoom();

    if (camera && camera?.disk?.freesize < camera?.disk?.totalsize * 0.1) {
      setDiskSpaceWarningText("LOW DISK SPACE")
    } else {
      setDiskSpaceWarningText("");
    }
  }, [camera]);


  const StatusIcon = ({ icon: Icon, id, value, tooltipText, isWarning }) => (
    <div className="d-inline-block text-center mx-2" style={{ width: '60px' }}>
      <div id={id}>
        <Icon size={24} />
        <div className={`small mt-1 ${isWarning ? "text-danger" : ""}`} style={{ wordBreak: 'break-word' }}>
          {value}
        </div>
      </div>
      <UncontrolledTooltip target={id} placement="top">
        {tooltipText}
      </UncontrolledTooltip>
    </div>
  );

  return (
    <Card className="mb-4 border rounded shadow-sm">
      <CardBody>
        <CardTitle tag="h5" className="text-primary mb-3">
          Camera: {camera?.details?.hostname}
        </CardTitle>
        <Row>
          <Col xs="12">
            <div className="position-relative" style={{ paddingBottom: '56.25%', height: 0 }}>
              <img
                src={`https://${camera?.details?.hostname}/axis-cgi/mjpg/video.cgi?resolution=1920x1080&compression=25&camera=1&fps=5&refreshKey=${refreshPreviewKey}`}
                alt="Camera Feed"
                className="position-absolute top-0 start-0 w-100 h-100 object-fit-cover"
              />
            </div>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col xs="12" className="d-flex justify-content-between align-items-center flex-wrap">
            <Button color="primary" size="sm" onClick={refreshPreview} className="mb-2">
              <RefreshCw size={16} className="me-1" /> Refresh Preview
            </Button>
            {shouldResetZoom && (
              <div className="d-flex align-items-center mb-2" style={{ width: '60%' }}>
                <Input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={cameraZoom}
                  onChange={handleZoomChange}
                  className="me-2"
                />
                <span>{(cameraZoom * 100).toFixed(0)}%</span>
              </div>
            )}
          </Col>
        </Row>
        <Row className="mt-2">
          <Col xs="12" className="d-flex justify-content-center align-items-start flex-wrap bg-light py-2">
            {shouldResetZoom && (
              <>
                <StatusIcon
                  icon={Folder}
                  id="networkShare"
                  value={camera?.networkShare?.Share}
                  tooltipText="Network share location"
                />
                <StatusIcon
                  icon={Database}
                  id="totalSpace"
                  value={camera?.disk?.totalsize > 0 ? (camera?.disk?.totalsize / 1024 / 1024).toFixed(2) + " GB" : "Loading"}
                  tooltipText="Total disk space"
                />
              </>
            )}
            <StatusIcon
              icon={HardDrive}
              id="remainingSpace"
              value={camera?.disk?.freesize > 0 ? (camera?.disk?.freesize / 1024 / 1024).toFixed(2) + " GB" : "Loading"}
              tooltipText="Remaining disk space"
              isWarning={camera?.disk?.freesize < camera?.disk?.totalsize * 0.1}
            />
            {shouldResetZoom && (
              <StatusIcon
                icon={Percent}
                id="remainingPercent"
                value={camera?.disk?.freesize > 0 ? ((camera?.disk?.freesize / camera?.disk?.totalsize) * 100).toFixed(2) + "%" : "Loading"}
                tooltipText="Percentage of remaining disk space"
                isWarning={camera?.disk?.freesize < camera?.disk?.totalsize * 0.1}
              />
            )}
            <StatusIcon
              icon={AlertTriangle}
              id="diskStatus"
              value={
                !!!diskSpaceWarningText ? (
                  <Badge color={getDiskStatusColor(camera?.disk?.status)}>
                    {camera?.disk?.status}
                  </Badge>
                ) : (
                  <span className="text-danger">{diskSpaceWarningText}</span>
                )
              }
              tooltipText="Current disk status"
            />
          </Col>
        </Row>
      </CardBody>
    </Card>
  )
}

export default AxisCameraDeviceCard